import type { Stripe } from '@stripe/stripe-js'

class StripeService {
  private static instance: StripeService
  private stripePromise: Promise<Stripe | null> | null = null
  private isInitializing = false
  private mountedComponents = 0

  private constructor() {}

  public static getInstance(): StripeService {
    if (!StripeService.instance) {
      StripeService.instance = new StripeService()
    }
    return StripeService.instance
  }

  public async getStripe(): Promise<Stripe | null> {
    this.mountedComponents++

    // If we already have a Stripe instance, return it
    if (this.stripePromise) {
      return this.stripePromise
    }

    // If we're already initializing, wait for it
    if (this.isInitializing) {
      return new Promise((resolve) => {
        const checkStripe = () => {
          if (this.stripePromise) {
            resolve(this.stripePromise)
          } else {
            setTimeout(checkStripe, 100)
          }
        }
        checkStripe()
      })
    }

    // Start initialization
    this.isInitializing = true

    try {
      const publishableKey = process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY
      if (!publishableKey) {
        throw new Error('Stripe publishable key is missing')
      }

      // Dynamically import Stripe
      const { loadStripe } = await import('@stripe/stripe-js')
      this.stripePromise = loadStripe(publishableKey)
      this.isInitializing = false
      return this.stripePromise
    } catch {
      this.isInitializing = false
      this.stripePromise = null
      return null
    }
  }

  public cleanup(): void {
    this.mountedComponents--

    // Only clean up if this was the last mounted component
    if (this.mountedComponents <= 0) {
      this.mountedComponents = 0 // Reset to ensure we don't go negative

      if (typeof window !== 'undefined') {
        // Remove Stripe controller iframes
        const stripeControllers = document.querySelectorAll(
          'iframe[name*="privateStripeController"]'
        )
        stripeControllers.forEach((iframe) => iframe.remove())

        // Remove Stripe metrics iframes
        const stripeMetrics = document.querySelectorAll(
          'iframe[name*="privateStripeMetricsController"]'
        )
        stripeMetrics.forEach((iframe) => iframe.remove())

        // Remove any other Stripe iframes
        const stripeIframes = document.querySelectorAll('iframe[src*="stripe.com"]')
        stripeIframes.forEach((iframe) => iframe.remove())

        // Remove any Stripe scripts
        const stripeScripts = document.querySelectorAll('script[src*="stripe.com"]')
        stripeScripts.forEach((script) => script.remove())

        // Remove any Stripe-related elements
        const stripeElements = document.querySelectorAll('[class*="stripe"]')
        stripeElements.forEach((element) => element.remove())

        // Clean up storage
        try {
          // Local storage cleanup
          Object.keys(localStorage).forEach((key) => {
            if (key.toLowerCase().includes('stripe')) {
              localStorage.removeItem(key)
            }
          })

          // Session storage cleanup
          Object.keys(sessionStorage).forEach((key) => {
            if (key.toLowerCase().includes('stripe')) {
              sessionStorage.removeItem(key)
            }
          })
        } catch {
          // Ignore storage errors
        }
      }

      // Reset the service state
      this.stripePromise = null
      this.isInitializing = false
    }
  }
}

export const stripeService = StripeService.getInstance()
